import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const SECTIONS = [
  {
    title: 'Acceptance of Terms',
    body: `By creating an account or using Protovera, you agree to these Terms of Service. If you do not agree, do not use the service.`,
  },
  {
    title: 'Description of Service',
    body: `Protovera is an AI-assisted research tool that helps life sciences researchers search academic literature and generate research protocols, hypotheses, literature reviews, and grant-ready content. AI outputs are meant to assist — not replace — professional scientific judgment.`,
  },
  {
    title: 'User Responsibilities',
    body: `You are responsible for verifying all AI-generated content before use in research, publications, or grant applications. Protovera does not guarantee the accuracy, completeness, or fitness of any generated protocol or analysis. You must not use Protovera for any unlawful purpose or to generate content that violates third-party rights.`,
  },
  {
    title: 'Intellectual Property',
    body: `You retain ownership of the content you upload (papers, notes, workspace data). AI-generated outputs produced using your inputs are yours to use. Protovera retains rights to the platform, codebase, and underlying systems.`,
  },
  {
    title: 'Subscriptions and Billing',
    body: `Paid plans are billed monthly or annually via Stripe. Subscriptions renew automatically unless cancelled before the next billing date. Refunds are handled on a case-by-case basis — contact info@protovera.org within 7 days of a charge if you believe there is an error.`,
  },
  {
    title: 'Free Plan Limitations',
    body: `The free plan includes 5 searches per day and 3 lifetime protocol generations. These limits exist to ensure fair use and may change with reasonable notice.`,
  },
  {
    title: 'AI Disclaimer',
    body: `AI-generated protocols, hypotheses, and analyses are based on the papers in your workspace and are not validated by domain experts. Always review outputs with appropriate scientific scrutiny. Protovera is not liable for research outcomes based on AI-generated content.`,
  },
  {
    title: 'Service Availability',
    body: `We aim for high availability but do not guarantee uninterrupted service. Scheduled maintenance, third-party API outages (e.g., OpenRouter, PubMed), or infrastructure issues may cause temporary downtime.`,
  },
  {
    title: 'Termination',
    body: `We reserve the right to suspend or terminate accounts that violate these terms. You may close your account at any time by contacting info@protovera.org.`,
  },
  {
    title: 'Limitation of Liability',
    body: `To the maximum extent permitted by law, Protovera shall not be liable for indirect, incidental, or consequential damages arising from use of the service. Our total liability shall not exceed the amount you paid in the 12 months preceding the claim.`,
  },
  {
    title: 'Changes to Terms',
    body: `We may update these terms with reasonable notice via email or in-app notification. Continued use of Protovera after changes take effect constitutes acceptance.`,
  },
  {
    title: 'Contact',
    body: `Questions about these terms: info@protovera.org.`,
  },
];

export default function Terms() {
  useEffect(() => {
    document.title = 'Terms of Service — Protovera';
    const el = document.querySelector('link[rel="canonical"]');
    if (el) el.setAttribute('href', `${window.location.origin}/terms`);
    return () => {
      document.title = 'Protovera — AI Research Protocol Generator for Life Sciences';
      if (el) el.setAttribute('href', `${window.location.origin}/`);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <header className="bg-white" style={{ boxShadow: 'rgba(0,0,0,0.08) 0px -1px 0px 0px inset' }}>
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-sm font-semibold text-[#171717]" style={{ letterSpacing: '-0.02rem' }}>
            Protovera
          </Link>
          <Link to="/register" className="text-sm bg-[#171717] text-white px-4 py-1.5 rounded-md font-medium hover:bg-[#2e2e2e] transition-colors">
            Get Started
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-semibold text-[#171717] mb-2" style={{ letterSpacing: '-1.28px' }}>
          Terms of Service
        </h1>
        <p className="text-sm text-[#808080] mb-10">Last updated: June 2026</p>

        <div className="space-y-8">
          {SECTIONS.map((s) => (
            <section key={s.title}>
              <h2 className="text-base font-semibold text-[#171717] mb-2" style={{ letterSpacing: '-0.32px' }}>
                {s.title}
              </h2>
              <p className="text-sm text-[#4d4d4d] leading-relaxed">{s.body}</p>
            </section>
          ))}
        </div>

        <div className="mt-12 pt-6 border-t border-[#ebebeb] flex gap-4 text-xs text-[#808080]">
          <Link to="/privacy" className="hover:text-[#171717] transition-colors">Privacy Policy</Link>
          <Link to="/" className="hover:text-[#171717] transition-colors">← Back to Protovera</Link>
        </div>
      </main>
    </div>
  );
}
